import React from 'react';
import { Skeleton } from '../../../Common/Skeleton';
import { getTabCount } from '../shared/orderTabs';
import { getStatusTone, TONE_MARK } from '../shared/orderStatus';

/*
 * The status filter strip.
 *
 * Lifecycle tabs carry their status's own tone dot, so the strip and the rows
 * agree on what colour a state is. The old version invented eight separate tab
 * hues — slate, amber, blue, indigo, emerald, rose, red, orange — none of which
 * matched the pills in the table.
 *
 * Counts distinguish "zero" from "unknown": a number renders whenever the API
 * gave one, including 0, and nothing renders when it didn't. Previously both
 * cases showed a blank chip, which is why Failed and Abandoned looked identical
 * to a tab that genuinely had no orders.
 */

const TabSkeleton = () => (
  <div className="flex flex-wrap items-center gap-1.5" aria-hidden="true">
    {[72, 96, 92, 118, 96, 92].map((width, index) => (
      <Skeleton
        key={index}
        delay={index}
        className="h-[30px] rounded-full"
        style={{ width }}
      />
    ))}
  </div>
);

const OrderStatusTabs = ({
  tabs,
  activeKey,
  headerCount,
  totalFromPagination,
  onSelect,
  isLoading,
}) => {
  if (isLoading) return <TabSkeleton />;

  let paymentDividerDrawn = false;

  return (
    <div
      role="tablist"
      aria-label="Filter orders by status"
      className="flex flex-wrap items-center gap-1.5"
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        const count = getTabCount({ tab, headerCount, totalFromPagination });

        // Payment outcomes are a different axis from lifecycle stages, so they
        // sit behind a rule rather than reading as more of the same.
        const needsDivider = tab.kind === 'payment' && !paymentDividerDrawn;
        if (needsDivider) paymentDividerDrawn = true;

        const dotColor =
          tab.kind === 'status'
            ? TONE_MARK[getStatusTone(tab.statusId)]
            : '#A9B7BF';

        return (
          <React.Fragment key={tab.key}>
            {needsDivider && (
              <span
                aria-hidden="true"
                className="w-px h-5 bg-[#DFE6E9] mx-1 shrink-0"
              />
            )}
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(tab.key)}
              className={[
                'inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5',
                'text-[12.5px] font-semibold border transition-colors cursor-pointer',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent',
                isActive
                  ? 'bg-nysmah-dark border-nysmah-dark text-white'
                  : 'bg-white border-[#DFE6E9] text-[#46555C] hover:border-[#C4CFD5] hover:text-nysmah-dark',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: dotColor }}
              />
              <span>{tab.label}</span>
              {count != null && (
                <span
                  className={`tabular-nums font-medium ${
                    isActive
                      ? 'text-white/65'
                      : count === 0
                        ? 'text-[#B6C2C8]'
                        : 'text-[#6B7C86]'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default OrderStatusTabs;
